import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useRef, useState, type ChangeEvent } from 'react';
import { useTranslation } from 'react-i18next';
import { importRunListSchema, importSummarySchema, type ImportSummary } from '@competa/shared';
import { apiRequest } from '../../api/client';
import { Badge } from '../../components/Badge';
import { Button } from '../../components/Button';
import { errorMessage } from '../../i18n/errors';
import styles from './Admin.module.css';

/** Schlüssel des Importprotokolls im Query-Cache. */
const RUNS_KEY = ['admin', 'imports'] as const;

/** Höchstzahl angezeigter Hinweise der Vorschau (der Rest steht im Protokoll). */
const MAX_ISSUES_SHOWN = 50;

/** Eine Vorschau mit der zugehörigen Datei (für die Bestätigung). */
interface Preview {
  file: File;
  summary: ImportSummary;
}

/**
 * Administration der Wettbewerbsliste: Datei auswählen → Vorschau (nichts wird gespeichert) →
 * Import bestätigen. Darunter das Importprotokoll mit Hinweis, falls der letzte echte Import
 * fehlgeschlagen ist (auch beim automatischen Import).
 */
export function SalonsAdminPage() {
  const { t, i18n } = useTranslation();
  const queryClient = useQueryClient();
  const input = useRef<HTMLInputElement>(null);
  const [preview, setPreview] = useState<Preview | null>(null);
  const [done, setDone] = useState<ImportSummary | null>(null);

  const runs = useQuery({
    queryKey: RUNS_KEY,
    queryFn: () => apiRequest('GET', '/admin/imports?limit=20', { schema: importRunListSchema }),
  });

  const upload = useMutation({
    mutationFn: ({ file, dryRun }: { file: File; dryRun: boolean }) => {
      const form = new FormData();
      form.append('file', file);
      return apiRequest('POST', `/admin/salons/import?dryRun=${dryRun}`, {
        form,
        schema: importSummarySchema,
      });
    },
  });

  /**
   * Berechnet nach der Dateiauswahl die Vorschau.
   * @param event Änderung des Dateifelds.
   */
  async function handleFile(event: ChangeEvent<HTMLInputElement>): Promise<void> {
    const file = event.target.files?.[0];
    event.target.value = '';
    if (!file) return;
    setDone(null);
    setPreview(null);
    upload.reset();
    try {
      const summary = await upload.mutateAsync({ file, dryRun: true });
      setPreview({ file, summary });
    } catch {
      // Der Fehler steht in `upload.error` und wird unten angezeigt.
    }
    await queryClient.invalidateQueries({ queryKey: RUNS_KEY });
  }

  /** Führt den Import mit der zuvor geprüften Datei tatsächlich aus. */
  async function confirm(): Promise<void> {
    if (!preview) return;
    try {
      const summary = await upload.mutateAsync({ file: preview.file, dryRun: false });
      setDone(summary);
      setPreview(null);
    } catch {
      // Fehleranzeige über `upload.error`.
    }
    await queryClient.invalidateQueries({ queryKey: RUNS_KEY });
    await queryClient.invalidateQueries({ queryKey: ['salons'] });
  }

  const lastRealRun = runs.data?.runs.find((run) => !run.dryRun);
  const summary = preview?.summary;

  return (
    <div>
      <h2>{t('admin.salons.title')}</h2>
      <p className={styles.note}>{t('admin.salons.intro')}</p>

      {lastRealRun?.failed && (
        <p role="alert" className={styles.error}>
          {t('admin.salons.lastFailed')}
        </p>
      )}

      <input
        ref={input}
        type="file"
        accept=".pdf,.xlsx"
        hidden
        aria-label={t('admin.salons.chooseFile')}
        onChange={(event) => void handleFile(event)}
      />
      <Button onClick={() => input.current?.click()} disabled={upload.isPending}>
        {t('admin.salons.chooseFile')}
      </Button>

      {upload.isPending && (
        <p role="status">{preview ? t('admin.salons.importing') : t('admin.salons.previewing')}</p>
      )}
      {upload.isError && (
        <p role="alert" className={styles.error}>
          {errorMessage(t, upload.error)}
        </p>
      )}
      {done && (
        <p role="status">
          {t('admin.salons.done', { inserted: done.inserted, updated: done.updated })}
        </p>
      )}

      {summary && preview && (
        <section aria-label={t('admin.salons.previewTitle', { file: preview.file.name })}>
          <h3>{t('admin.salons.previewTitle', { file: preview.file.name })}</h3>
          <dl className={styles.stats}>
            <dt>{t('admin.salons.total')}</dt>
            <dd>{summary.total}</dd>
            <dt>{t('admin.salons.inserted')}</dt>
            <dd>{summary.inserted}</dd>
            <dt>{t('admin.salons.updated')}</dt>
            <dd>{summary.updated}</dd>
            <dt>{t('admin.salons.unchanged')}</dt>
            <dd>{summary.unchanged}</dd>
            <dt>{t('admin.salons.skipped')}</dt>
            <dd>{summary.skipped}</dd>
            <dt>{t('admin.salons.countryUnmatched')}</dt>
            <dd>{summary.countryUnmatched}</dd>
          </dl>
          <details>
            <summary>{t('admin.salons.issues', { count: summary.issues.length })}</summary>
            {summary.issues.length === 0 ? (
              <p>{t('admin.salons.noIssues')}</p>
            ) : (
              <ul>
                {summary.issues.slice(0, MAX_ISSUES_SHOWN).map((issue, index) => (
                  <li key={`${issue.fiapNumber}-${index}`}>
                    {issue.fiapNumber ? `${issue.fiapNumber}: ` : ''}
                    {issue.message}
                  </li>
                ))}
              </ul>
            )}
          </details>
          <div className={styles.actions}>
            <Button onClick={() => void confirm()} disabled={upload.isPending}>
              {t('admin.salons.confirm')}
            </Button>
            <Button variant="secondary" onClick={() => setPreview(null)}>
              {t('common.cancel')}
            </Button>
          </div>
        </section>
      )}

      <h3>{t('admin.salons.log')}</h3>
      {runs.isPending && <p role="status">{t('common.loading')}</p>}
      {runs.data && (
        <div className={styles.tableWrap}>
          <table className={styles.table}>
            <thead>
              <tr>
                <th>{t('admin.salons.logWhen')}</th>
                <th>{t('admin.salons.logKind')}</th>
                <th>{t('admin.salons.logFile')}</th>
                <th>{t('admin.salons.logResult')}</th>
                <th>{t('admin.salons.logUser')}</th>
              </tr>
            </thead>
            <tbody>
              {runs.data.runs.length === 0 && (
                <tr>
                  <td colSpan={5}>{t('admin.salons.logEmpty')}</td>
                </tr>
              )}
              {runs.data.runs.map((run) => (
                <tr key={run.id}>
                  <td>{new Date(run.createdAt).toLocaleString(i18n.language)}</td>
                  <td>{t(`admin.salons.kind_${run.kind}`, { defaultValue: run.kind })}</td>
                  <td>{run.filename ?? '–'}</td>
                  <td>
                    {run.failed ? (
                      <Badge tone="danger">{t('admin.salons.statusFailed')}</Badge>
                    ) : run.dryRun ? (
                      <Badge tone="neutral">{t('admin.salons.statusPreview')}</Badge>
                    ) : (
                      <Badge tone="success">{t('admin.salons.statusOk')}</Badge>
                    )}{' '}
                    {!run.failed &&
                      `${run.inserted} ${t('admin.salons.inserted').toLowerCase()}, ${run.updated} ${t('admin.salons.updated').toLowerCase()}`}
                  </td>
                  <td>{run.username ?? t('admin.salons.automatic')}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
